import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Calendar, FileX, Trash2, Ban } from 'lucide-react';
import { auditApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

interface DeletedRow {
  audit_id?: string | null;
  invoice: string;
  method: 'Deleted' | 'Cancelled' | string;
  action_type?: string;
  deleted_at: string;
  deleted_by: string;
  deleted_by_name: string;
  invoice_date?: string | null;
  customer?: string;
  customer_name?: string;
  net_total: number;
  tax_amount: number;
  paid_amount?: number;
  status_before?: string;
  linked_returns?: string[];
  source?: string;
}

interface DeletedReport {
  summary: {
    count: number;
    total_amount: number;
    total_tax: number;
    deleted_count: number;
    cancelled_count: number;
  };
  rows: DeletedRow[];
}

export default function DeletedInvoicesSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const { data, isLoading, error } = useQuery({
    queryKey: ['deletedInvoicesReport', from_date, to_date],
    queryFn: async () => {
      const res = await auditApi.getDeletedInvoices({ from_date, to_date });
      return res.data.message as DeletedReport;
    },
    enabled: !!from_date && !!to_date,
  });

  const handleExport = () => {
    if (!data?.rows?.length) return;
    downloadCsv(
      `Deleted_Invoices_${from_date}_to_${to_date}.csv`,
      [
        { key: 'invoice', label: 'Invoice' },
        { key: 'method', label: 'Action' },
        { key: 'deleted_at', label: 'Deleted At' },
        { key: 'deleted_by_name', label: 'Deleted By' },
        { key: 'invoice_date', label: 'Invoice Date' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'net_total', label: 'Amount' },
        { key: 'tax_amount', label: 'VAT / Tax' },
        { key: 'status_before', label: 'Status Before' },
        { key: 'linked_returns', label: 'Linked Returns Removed' },
      ],
      data.rows.map((r) => ({
        invoice: r.invoice,
        method: r.method,
        deleted_at: r.deleted_at,
        deleted_by_name: r.deleted_by_name || r.deleted_by,
        invoice_date: r.invoice_date || '',
        customer_name: r.customer_name || r.customer || '',
        net_total: Number(r.net_total || 0).toFixed(2),
        tax_amount: Number(r.tax_amount || 0).toFixed(2),
        status_before: r.status_before || '',
        linked_returns: (r.linked_returns || []).join('; '),
      })),
    );
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p>{t('reports.deletedInvoicesLoadFail', 'Failed to load deleted invoices report.')}</p>
      </div>
    );
  }

  const summary = data?.summary;
  const rows = data?.rows || [];

  return (
    <div className="space-y-6">
      <ReportDateRangeFilter
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
      />

      {!from_date || !to_date ? (
        <div className="card p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Select Date Range</h3>
          <p className="text-gray-500">Choose a period to list cancelled and deleted sales.</p>
        </div>
      ) : isLoading ? (
        <div className="card p-12 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-500 max-w-2xl">
              Shows hard deletes (Sales Hub Reset) and POS cancellations, with amount, date/time, and who
              performed the action. Use for stock/VAT audit trails.
            </p>
            <ReportExportButton onClick={handleExport} disabled={!rows.length} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card p-5">
              <p className="text-sm text-gray-500 mb-1">Total removed</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.count ?? 0}</p>
            </div>
            <div className="card p-5">
              <p className="text-sm text-gray-500 mb-1">Hard deleted</p>
              <p className="text-2xl font-bold text-rose-700">{summary?.deleted_count ?? 0}</p>
            </div>
            <div className="card p-5">
              <p className="text-sm text-gray-500 mb-1">Cancelled (void)</p>
              <p className="text-2xl font-bold text-amber-700">{summary?.cancelled_count ?? 0}</p>
            </div>
            <div className="card p-5">
              <p className="text-sm text-gray-500 mb-1">Amount removed</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatMoney(summary?.total_amount || 0)}
              </p>
              {(summary?.total_tax || 0) > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Tax {formatMoney(summary?.total_tax || 0)}
                </p>
              )}
            </div>
          </div>

          <div className="card overflow-hidden">
            {rows.length === 0 ? (
              <div className="py-16 text-center">
                <FileX className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900">No deleted or cancelled sales</h3>
                <p className="text-sm text-gray-500 mt-1">Nothing in this date range.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Invoice</th>
                      <th className="py-3 px-4">Action</th>
                      <th className="py-3 px-4">Deleted at</th>
                      <th className="py-3 px-4">Deleted by</th>
                      <th className="py-3 px-4">Customer</th>
                      <th className="py-3 px-4 text-right">Amount</th>
                      <th className="py-3 px-4 text-right">Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {rows.map((r, idx) => (
                      <tr key={`${r.invoice}-${r.deleted_at}-${idx}`} className="hover:bg-gray-50/60">
                        <td className="py-3 px-4">
                          <span className="font-mono font-medium text-gray-900">{r.invoice}</span>
                          {r.invoice_date && (
                            <span className="block text-xs text-gray-400">Sale {r.invoice_date}</span>
                          )}
                          {(r.linked_returns || []).length > 0 && (
                            <span className="block text-xs text-amber-600 mt-0.5">
                              Returns removed: {(r.linked_returns || []).join(', ')}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {r.method === 'Deleted' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700">
                              <Trash2 className="w-3 h-3" /> Deleted
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800">
                              <Ban className="w-3 h-3" /> Cancelled
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">{r.deleted_at}</td>
                        <td className="py-3 px-4 text-gray-800">{r.deleted_by_name || r.deleted_by}</td>
                        <td className="py-3 px-4 text-gray-600">{r.customer_name || r.customer || '—'}</td>
                        <td className="py-3 px-4 text-right font-medium tabular-nums">
                          {formatMoney(r.net_total || 0)}
                        </td>
                        <td className="py-3 px-4 text-right tabular-nums text-gray-600">
                          {formatMoney(r.tax_amount || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
